import { Link } from 'react-router-dom';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://yoybuy.guru';
const pageUrl = `${siteUrl}/blog/yoybuy-spreadsheet-instruktsiya`;

const articleSchema = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: 'YOYBUY Spreadsheet — Полное руководство для новичков 2026',
  description: 'Подробная инструкция по использованию YOYBUY Spreadsheet для заказа товаров из Китая.',
  author: { '@type': 'Organization', name: 'yoybuy.guru' },
  datePublished: '2026-07-05',
  dateModified: '2026-07-05',
  inLanguage: 'ru',
  mainEntityOfPage: { '@type': 'WebPage', '@id': pageUrl },
};

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Главная', item: siteUrl + '/' },
    { '@type': 'ListItem', position: 2, name: 'YOYBUY Spreadsheet — Полное руководство для новичков', item: pageUrl },
  ],
};

const faqPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Сколько стоит доставка через YOYBUY Spreadsheet?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Стоимость зависит от веса посылки, способа доставки и страны назначения. Точную цену вы увидите после консолидации товаров на складе.',
      },
    },
    {
      '@type': 'Question',
      name: 'Можно ли заказать один товар, или только оптом?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'YOYBUY Spreadsheet работает с заказами любого объёма — от одной единицы до крупных оптовых партий.',
      },
    },
    {
      '@type': 'Question',
      name: 'Как долго идёт посылка из Китая?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'В среднем 7–20 дней. Экспресс — 5–10 дней, авиапочта — 10–20 дней, морская доставка — 30–45 дней.',
      },
    },
    {
      '@type': 'Question',
      name: 'Что делать, если товар пришёл с браком?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Брак выявляется на этапе проверки на складе YOYBUY. Товар возвращается продавцу, вы получаете возврат средств.',
      },
    },
  ],
};

export default function BlogPage() {
  return (
    <>
      <title>YOYBUY Spreadsheet — Полное руководство для новичков 2026 | yoybuy.guru</title>
      <meta name="description" content="Подробная инструкция по использованию YOYBUY Spreadsheet для заказа товаров из Китая. Пошаговое руководство, сравнение с другими сервисами, советы по выбору товаров и доставке." />
      <meta name="keywords" content="yoybuy spreadsheet инструкция, yoybuy руководство, заказ товаров из Китая spreadsheet, kaspreadsheet, покупка из Китая таблица" />
      <link rel="canonical" href={pageUrl} />
      <meta property="og:title" content="YOYBUY Spreadsheet — Полное руководство для новичков 2026" />
      <meta property="og:description" content="Подробная инструкция по использованию YOYBUY Spreadsheet для заказа товаров из Китая." />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:type" content="article" />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqPageSchema) }}
      />

      <header className="sticky top-0 z-40 bg-background-50/95 backdrop-blur-sm border-b border-background-200/50">
        <div className="max-w-5xl mx-auto px-4 md:px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-foreground-900 hover:text-primary-600 transition-colors cursor-pointer">
            <i className="ri-arrow-left-line w-5 h-5 flex items-center justify-center" />
            <span className="text-sm font-semibold font-heading">yoybuy.guru</span>
          </Link>
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="whitespace-nowrap inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-primary-500 text-background-50 text-xs font-semibold hover:bg-primary-600 transition-colors cursor-pointer"
          >
            <i className="ri-shopping-bag-3-line w-3.5 h-3.5 flex items-center justify-center" />
            YOYBUY SHEET
          </a>
        </div>
      </header>

      <main className="py-8 md:py-12 px-4 md:px-6">
        <article className="max-w-3xl mx-auto">
          <header className="mb-8 md:mb-10">
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <span className="px-2.5 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium">Руководство</span>
              <span className="px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium">2026</span>
              <span className="text-xs text-foreground-400">Обновлено: Июль 2026</span>
            </div>
            <h1 className="text-2xl md:text-4xl font-heading font-extrabold text-foreground-900 leading-tight mb-3">
              YOYBUY Spreadsheet: Полное руководство для новичников
            </h1>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
              В этом руководстве мы подробно разберём, как работает <strong>YOYBUY Spreadsheet</strong> — система заказов товаров из Китая через таблицу, которая делает процесс покупки простым, быстрым и контролируемым.
            </p>
          </header>

          <img
            src="https://readdy.ai/api/search-image?query=Organized%20minimalist%20workspace%20with%20a%20laptop%20displaying%20a%20spreadsheet%20interface%20on%20screen%20surrounded%20by%20product%20catalog%20images%20and%20shipping%20boxes%20in%20background%2C%20soft%20natural%20daylight%20illuminating%20the%20desk%2C%20warm%20beige%20and%20cream%20tones%20throughout%2C%20Scandinavian%20interior%20design%20aesthetic%2C%20organized%20and%20calm%20atmosphere%2C%20modern%20ecommerce%20management%20concept%2C%20editorial%20photography%20style%20with%20soft%20shadows%20and%20gentle%20depth%20of%20field%20effect&width=1200&height=600&seq=yoybuy-blog-hero-002&orientation=landscape"
            alt="YOYBUY Spreadsheet руководство — система заказов из Китая"
            title="YOYBUY Spreadsheet — полное руководство по заказу товаров из Китая"
            className="w-full h-auto rounded-lg mb-8 md:mb-10"
          />

          <nav className="p-4 md:p-5 rounded-lg bg-background-100/70 border border-background-200/50 mb-8">
            <h4 className="text-sm font-heading font-semibold text-foreground-900 mb-3">Содержание</h4>
            <ul className="space-y-1.5 text-sm text-foreground-600">
              <li><a href="#what-is" className="hover:text-primary-600 transition-colors">1. Что такое YOYBUY Spreadsheet</a></li>
              <li><a href="#how-works" className="hover:text-primary-600 transition-colors">2. Как работает система заказов</a></li>
              <li><a href="#advantages" className="hover:text-primary-600 transition-colors">3. Преимущества YOYBUY Spreadsheet</a></li>
              <li><a href="#comparison" className="hover:text-primary-600 transition-colors">4. Сравнение с другими сервисами</a></li>
              <li><a href="#best-products" className="hover:text-primary-600 transition-colors">5. Лучшие товары для заказа</a></li>
              <li><a href="#delivery" className="hover:text-primary-600 transition-colors">6. Как работает доставка из Китая</a></li>
              <li><a href="#safety" className="hover:text-primary-600 transition-colors">7. Безопасность и надёжность</a></li>
              <li><a href="#faq" className="hover:text-primary-600 transition-colors">8. Часто задаваемые вопросы</a></li>
            </ul>
          </nav>

          <div className="space-y-8 text-foreground-700 text-sm md:text-base">

            <section id="what-is">
              <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-3">1. Что такое YOYBUY Spreadsheet</h2>
              <p className="mb-3 leading-relaxed">
                <strong>YOYBUY Spreadsheet</strong> — это специализированная система для заказа товаров из Китая, построенная на использовании структурированных электронных таблиц. В отличие от традиционных посредников, где каждый заказ требует отдельного общения в чате и ручного заполнения форм, <strong>YOYBUY Spreadsheet</strong> предлагает принципиально иной подход: вы работаете с единой таблицей, в которую добавляете все необходимые товары, а система автоматически обрабатывает каждый пункт.
              </p>
              <p className="mb-3 leading-relaxed">
                Идея проста: вместо того чтобы тратить часы на переписку с агентом по каждому товару, вы просто заполняете строки в таблице — указываете ссылку на товар, нужный размер, цвет, количество и особые пожелания. YOYBUY берёт на себя всё остальное: выкуп у продавца, проверку качества на складе, консолидацию посылок и международную доставку.
              </p>
              <p className="leading-relaxed">
                На сегодняшний день это один из самых эффективных способов организации закупок из Китая. Основной сайт системы — <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-accent-600 hover:text-accent-700 font-medium">kaspreadsheet.com</a>, где вы можете начать работу.
              </p>
            </section>

            <section id="how-works">
              <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-3">2. Как работает система заказов</h2>
              <p className="mb-4 leading-relaxed">
                Процесс работы с <strong>YOYBUY Spreadsheet</strong> состоит из нескольких простых этапов.
              </p>
              <div className="space-y-4">
                {[
                  { num: '1', title: 'Поиск товара на китайских площадках', desc: 'Найдите нужные товары на Taobao, 1688, Weidian. Скопируйте ссылку на страницу товара для заполнения таблицы.' },
                  { num: '2', title: 'Заполнение YOYBUY Spreadsheet', desc: 'Вставьте ссылку, укажите размер, цвет, количество. Одна строка — один товар. Добавляйте десятки позиций в одну таблицу.' },
                  { num: '3', title: 'Отправка таблицы агенту', desc: 'Таблица отправляется персональному агенту YOYBUY. Система рассчитывает стоимость с учётом доставки по Китаю.' },
                  { num: '4', title: 'Выкуп и проверка качества', desc: 'Агент выкупает позиции, на складе проводится проверка: сверка с описанием, осмотр на брак, фотофиксация.' },
                  { num: '5', title: 'Консолидация и международная доставка', desc: 'Товары объединяются в одну посылку. Вы выбираете способ доставки, и заказ отправляется в вашу страну.' },
                ].map((step) => (
                  <div key={step.num} className="flex gap-3 md:gap-4 p-4 rounded-lg bg-background-100/70 border border-background-200/50">
                    <span className="flex-shrink-0 w-8 h-8 md:w-9 md:h-9 rounded-full bg-accent-500 text-background-50 flex items-center justify-center text-sm font-bold">
                      {step.num}
                    </span>
                    <div>
                      <h3 className="text-sm md:text-base font-heading font-semibold text-foreground-900 mb-1">{step.title}</h3>
                      <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section id="advantages">
              <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-3">3. Преимущества YOYBUY Spreadsheet</h2>
              <p className="mb-4 leading-relaxed">
                Почему стоит выбрать именно <strong>YOYBUY Spreadsheet</strong>? Ключевые преимущества в таблице ниже.
              </p>
              <div className="overflow-x-auto rounded-lg border border-background-200/70">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-background-200/50">
                      <th className="text-left px-4 py-3 font-semibold text-foreground-800">Характеристика</th>
                      <th className="text-left px-4 py-3 font-semibold text-foreground-800">YOYBUY Spreadsheet</th>
                      <th className="text-left px-4 py-3 font-semibold text-foreground-800">Ручной заказ через чат</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ['Скорость оформления', 'Высокая — массовое добавление', 'Низкая — по одному товару'],
                      ['Вероятность ошибок', 'Минимальная', 'Высокая (человеческий фактор)'],
                      ['Удобство', 'Очень удобно', 'Среднее'],
                      ['Отслеживание статуса', 'Полное — в реальном времени', 'Частичное'],
                      ['Контроль расходов', 'Прозрачный', 'Требует уточнений'],
                      ['Подходит для опта', 'Идеально', 'Неудобно'],
                    ].map((row, i) => (
                      <tr key={i} className="border-t border-background-100">
                        <td className="px-4 py-2.5 text-foreground-700">{row[0]}</td>
                        <td className="px-4 py-2.5 text-accent-700 font-medium">{row[1]}</td>
                        <td className="px-4 py-2.5 text-foreground-500">{row[2]}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section id="comparison">
              <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-3">4. Сравнение YOYBUY Spreadsheet с аналогами</h2>
              <p className="mb-4 leading-relaxed">
                Чтобы вам было проще сделать выбор, мы подготовили сравнение <strong>YOYBUY Spreadsheet</strong> с аналогами.
              </p>
              <div className="overflow-x-auto rounded-lg border border-background-200/70">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-background-200/50">
                      <th className="text-left px-4 py-3 font-semibold text-foreground-800">Критерий</th>
                      <th className="text-left px-4 py-3 font-semibold text-primary-700">YOYBUY</th>
                      <th className="text-left px-4 py-3 font-semibold text-foreground-800">Другие сервисы</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ['Цена услуг', 'Низкая', 'Средняя или высокая'],
                      ['Удобство интерфейса', 'Высокое (таблицы)', 'Среднее (чат)'],
                      ['Скорость обработки', 'Высокая', 'Средняя'],
                      ['Поддержка клиентов', 'Персональный агент', 'Общая поддержка'],
                      ['Проверка качества', 'Обязательная', 'Не всегда'],
                      ['Прозрачность цен', 'Полная', 'Частичная'],
                    ].map((row, i) => (
                      <tr key={i} className="border-t border-background-100">
                        <td className="px-4 py-2.5 text-foreground-700">{row[0]}</td>
                        <td className="px-4 py-2.5 text-primary-700 font-medium">{row[1]}</td>
                        <td className="px-4 py-2.5 text-foreground-500">{row[2]}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section id="best-products">
              <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-3">5. Лучшие категории товаров для заказа</h2>
              <p className="mb-4 leading-relaxed">
                Через <strong>YOYBUY Spreadsheet</strong> можно заказать любые товары. Вот самые популярные категории.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  { cat: 'Обувь', desc: 'Кроссовки, ботинки, туфли — огромный выбор по ценам значительно ниже европейских.', margin: 'Высокая', popularity: 'Очень высокая' },
                  { cat: 'Одежда и худи', desc: 'Футболки, свитера, куртки, джерси — отличное соотношение цены и качества.', margin: 'Высокая', popularity: 'Очень высокая' },
                  { cat: 'Аксессуары', desc: 'Сумки, ремни, очки, часы — стильные дополнения с низкой стоимостью доставки.', margin: 'Очень высокая', popularity: 'Высокая' },
                  { cat: 'Электроника', desc: 'Гаджеты, наушники, умные часы — передовые технологии по доступным ценам.', margin: 'Средняя', popularity: 'Высокая' },
                ].map((item) => (
                  <div key={item.cat} className="p-4 rounded-lg bg-background-100/70 border border-background-200/50">
                    <h3 className="text-sm font-heading font-semibold text-foreground-900 mb-1">{item.cat}</h3>
                    <p className="text-xs text-foreground-600 mb-2">{item.desc}</p>
                    <div className="flex items-center gap-3 text-xs">
                      <span className="text-accent-700 font-medium">Маржа: {item.margin}</span>
                      <span className="text-foreground-400">|</span>
                      <span className="text-foreground-600">Спрос: {item.popularity}</span>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section id="delivery">
              <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-3">6. Как работает доставка из Китая</h2>
              <p className="mb-4 leading-relaxed">
                <strong>YOYBUY Spreadsheet</strong> предлагает несколько вариантов международной доставки.
              </p>
              <div className="overflow-x-auto rounded-lg border border-background-200/70">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-background-200/50">
                      <th className="text-left px-4 py-3 font-semibold text-foreground-800">Этап</th>
                      <th className="text-left px-4 py-3 font-semibold text-foreground-800">Описание</th>
                      <th className="text-left px-4 py-3 font-semibold text-foreground-800">Сроки</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ['Выкуп у продавца', 'Агент покупает товар и ожидает его поступления на склад', '1–2 дня'],
                      ['Проверка качества', 'Осмотр товара, фотофиксация, сверка с заказом', '1–3 дня'],
                      ['Консолидация', 'Объединение всех товаров в одну посылку', '1 день'],
                      ['Международная отправка', 'Отправка выбранным способом (авиа, море, экспресс)', '7–20 дней'],
                    ].map((row, i) => (
                      <tr key={i} className="border-t border-background-100">
                        <td className="px-4 py-2.5 text-foreground-800 font-medium">{row[0]}</td>
                        <td className="px-4 py-2.5 text-foreground-600">{row[1]}</td>
                        <td className="px-4 py-2.5 text-accent-700 font-medium whitespace-nowrap">{row[2]}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section id="safety">
              <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-3">7. Безопасность и надёжность</h2>
              <p className="mb-3 leading-relaxed">
                Насколько безопасно пользоваться <strong>YOYBUY Spreadsheet</strong>? Рассмотрим все аспекты.
              </p>
              <ul className="list-disc pl-5 space-y-2 mb-4">
                <li><strong>Проверка качества:</strong> каждый товар проходит осмотр на складе. Брак возвращается продавцу, вы получаете возврат.</li>
                <li><strong>Защита платежей:</strong> транзакции через защищённые системы. Оплата после подтверждения заказа.</li>
                <li><strong>Персональный агент:</strong> личный менеджер решает все вопросы на русском языке.</li>
                <li><strong>Прозрачность:</strong> актуальный статус каждого товара в таблице, фотографии с проверки, трекинг-номер.</li>
              </ul>
              <div className="overflow-x-auto rounded-lg border border-background-200/70">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-background-200/50">
                      <th className="text-left px-4 py-3 font-semibold text-foreground-800">Фактор безопасности</th>
                      <th className="text-left px-4 py-3 font-semibold text-foreground-800">Уровень защиты</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ['Безопасность оплаты', 'Высокая — защищённые платёжные системы'],
                      ['Надёжность доставки', 'Высокая — отслеживание на всех этапах'],
                      ['Защита от брака', 'Полная — проверка качества и возврат'],
                      ['Поддержка клиентов', 'Персональный агент на русском языке'],
                    ].map((row, i) => (
                      <tr key={i} className="border-t border-background-100">
                        <td className="px-4 py-2.5 text-foreground-700">{row[0]}</td>
                        <td className="px-4 py-2.5 text-accent-700 font-medium">{row[1]}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section id="faq">
              <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-3">8. Часто задаваемые вопросы</h2>
              <div className="space-y-3">
                {[
                  { q: 'Сколько стоит доставка через YOYBUY Spreadsheet?', a: 'Стоимость зависит от веса посылки, способа доставки и страны назначения. Точную цену вы увидите после консолидации товаров на складе.' },
                  { q: 'Можно ли заказать один товар, или только оптом?', a: 'YOYBUY Spreadsheet работает с заказами любого объёма — от одной единицы до крупных оптовых партий.' },
                  { q: 'Как долго идёт посылка из Китая?', a: 'В среднем 7–20 дней. Экспресс — 5–10 дней, авиапочта — 10–20 дней, морская доставка — 30–45 дней.' },
                  { q: 'Что делать, если товар пришёл с браком?', a: 'Брак выявляется на этапе проверки на складе YOYBUY. Товар возвращается продавцу, вы получаете возврат средств.' },
                ].map((faq, i) => (
                  <details key={i} className="group rounded-lg border border-background-200/70 overflow-hidden">
                    <summary className="flex items-center justify-between px-4 py-3 cursor-pointer bg-background-100/50 hover:bg-background-100 transition-colors list-none">
                      <span className="text-sm font-medium text-foreground-800 pr-4">{faq.q}</span>
                      <i className="ri-add-line w-4 h-4 flex items-center justify-center text-foreground-400 group-open:hidden" />
                      <i className="ri-subtract-line w-4 h-4 flex items-center justify-center text-foreground-400 hidden group-open:flex" />
                    </summary>
                    <div className="px-4 pb-3">
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                    </div>
                  </details>
                ))}
              </div>
            </section>

            <section className="mt-10 p-5 md:p-6 rounded-lg bg-accent-100/50 border border-accent-200/60 text-center">
              <h2 className="text-lg font-heading font-bold text-foreground-900 mb-2">Готовы начать?</h2>
              <p className="text-sm text-foreground-600 mb-4 max-w-md mx-auto">
                Переходите на <strong>kaspreadsheet.com</strong> и начните использовать YOYBUY Spreadsheet уже сегодня.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="whitespace-nowrap inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-all cursor-pointer"
                >
                  <i className="ri-shopping-bag-3-line w-4 h-4 flex items-center justify-center" />
                  YOYBUY SHEET
                </a>
                <Link
                  to="/"
                  className="whitespace-nowrap inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-background-100 border border-background-300/60 text-foreground-800 font-medium text-sm hover:bg-background-200 transition-all cursor-pointer"
                >
                  <i className="ri-home-4-line w-4 h-4 flex items-center justify-center" />
                  На главную
                </Link>
              </div>
            </section>
          </div>
        </article>
      </main>

      <footer className="bg-secondary-500 text-background-50 py-8 px-4 md:px-6">
        <div className="max-w-3xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <Link to="/" className="text-sm font-heading font-semibold hover:text-background-100 transition-colors cursor-pointer">
            ← yoybuy.guru
          </Link>
          <p className="text-xs text-background-50/60">
            &copy; {new Date().getFullYear()} yoybuy.guru — Все права защищены.
          </p>
        </div>
      </footer>
    </>
  );
}